// CRM layer — admin console (added Phase 6-B).
// Tabs: performance dashboard · reminder settings · request types ·
//       user↔department mapping · the reassignment pool.

import React, { useCallback, useEffect, useState } from 'react';
import {
  crmGetDashboard, crmGetSettings, crmSaveSettings, crmGetTypes, crmCreateType, crmUpdateType,
  crmGetUserDepartments, crmAddUserDepartment, crmDeleteUserDepartment,
  crmGetUnassigned, crmAssign, crmNav, STAGE_LABEL, stagePillClass, fmtHours, fmtDateTime,
  crmAllTransfers, crmAsUserOpportunities, crmAsUserTransfers, crmUserNames
} from './crmApi';
import useAgentNames from './useAgentNames';

const TABS = [
  ['dashboard', 'لوحة الأداء'],
  ['settings', 'إعدادات التذكير'],
  ['types', 'أنواع الطلبات'],
  ['departments', 'أقسام الموظفين'],
  ['pool', 'غير المسندة'],
  ['transfers-monitor', 'متابعة التحويلات'],
  ['view-as', 'عرض بعين الموظف']
];

// Human label for transfer status. `pill` names the soft pastel token pill
// (crm.css) used to render it — same three statuses, calmer colours.
const TRANSFER_STATUS = {
  pending: { label: 'بانتظار الاستلام', pill: 'crm-pill-warn' },
  picked_up: { label: 'تم الاستلام', pill: 'crm-pill-accent' },
  done: { label: 'منتهي', pill: 'crm-pill-success' }
};

// Minutes -> readable Arabic duration for the settings preview.
const fmtMinutes = (mins) => {
  const m = Number(mins);
  if (!Number.isFinite(m) || m <= 0) return '—';
  if (m < 60) return `${m} دقيقة`;
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  if (r === 0) return `${h} ساعة`;
  return `${h} ساعة و${r} دقيقة`;
};

const CrmAdminPage = ({ setActivePage }) => {
  const [tab, setTab] = useState('dashboard');
  const [msg, setMsg] = useState('');
  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };

  return (
    // Batch 7 (Group B): the whole admin console now opts into the calm
    // identity. Styling scope only — every tab keeps its data, filters,
    // handlers and permissions exactly as they were.
    <div className="crm-scope crm-calm crm-calm-page">
      <div className="page-header"><h1 className="page-title">إدارة الـ CRM</h1></div>

      <div className="crm-adminbar">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`crm-tab${tab === k ? ' on' : ''}`}>{label}</button>
        ))}
      </div>

      {msg && <div className="crm-notice ok">{msg}</div>}

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'settings' && <SettingsTab flash={flash} />}
      {tab === 'types' && <TypesTab flash={flash} />}
      {tab === 'departments' && <DepartmentsTab flash={flash} />}
      {tab === 'pool' && <PoolTab flash={flash} setActivePage={setActivePage} />}
      {tab === 'transfers-monitor' && <TransfersMonitorTab />}
      {tab === 'view-as' && <ViewAsTab setActivePage={setActivePage} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
const DashboardTab = () => {
  const [d, setD] = useState(null);
  const [groupBy, setGroupBy] = useState('agent');
  const [preset, setPreset] = useState('all');

  const load = useCallback(async () => {
    const params = { groupBy };
    if (preset !== 'all') {
      const days = preset === 'today' ? 0 : preset === '7d' ? 7 : 30;
      const from = new Date(); from.setDate(from.getDate() - days);
      params.dateFrom = from.toISOString().slice(0, 10);
      params.dateTo = new Date().toISOString().slice(0, 10);
    }
    const res = await crmGetDashboard(params);
    setD(res.data);
  }, [groupBy, preset]);

  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="crm-empty">جاري التحميل...</div>;

  const sc = d.stageCounts.all;
  // `tone` replaces the old inline colour argument — same six KPIs, same order.
  const kpi = (n, l, tone) => (
    <div className="crm-kpi">
      <div className={`n ${tone || ''}`}>{n}</div>
      <div className="l">{l}</div>
    </div>
  );

  return (
    <div>
      <div className="crm-filterbar">
        {[['today', 'اليوم'], ['7d', 'آخر 7 أيام'], ['30d', 'آخر 30 يوم'], ['all', 'الكل']].map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${preset === k ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setPreset(k)}>{l}</button>
        ))}
      </div>

      <div className="crm-kpis">
        {kpi(d.totals.opportunities, 'إجمالي الفرص')}
        {kpi(d.totals.active, 'نشطة', 'accent')}
        {kpi(sc.contracted, 'تم التعاقد', 'success')}
        {kpi(sc.lost, 'خسارة', 'danger')}
        {kpi(sc.not_serious, 'غير جاد', 'warn')}
        {kpi(d.totals.conversionRate == null ? '—' : `${d.totals.conversionRate}%`, 'نسبة التحويل', 'accent')}
      </div>

      <ClassificationPanel d={d} />

      <div className="table-container" style={{ marginBottom: 16 }}>
        <div className="table-header"><div className="table-title">أداء الموظفين</div></div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr>
              <th>الموظف</th><th>نشطة</th><th>بانتظار التصنيف</th><th>تعاقد</th><th>غير جاد</th><th>خسارة</th>
              <th>سُحبت منه</th><th>نسبة التحويل</th><th>متوسط إنشاء ← تعاقد</th>
            </tr></thead>
            <tbody>
              {d.agents.length === 0 ? (
                <tr><td colSpan="9" className="crm-empty">لا توجد بيانات</td></tr>
              ) : d.agents.map((a) => (
                <tr key={a.extension}>
                  <td style={{ fontWeight: 600 }}>{a.extension}</td>
                  <td>{a.active}</td>
                  {/* Batch 3: a to-do count, kept out of `active` on the server
                      so it can never inflate pipeline volume. */}
                  <td>{a.pendingClassification
                    ? <span className="crm-pill crm-pill-pending">{a.pendingClassification}</span>
                    : 0}</td>
                  <td><span className="crm-pill crm-pill-success">{a.contracted}</span></td>
                  <td>{a.not_serious}</td>
                  <td>{a.lost}</td>
                  <td>{a.unassignedFrom}</td>
                  <td>{a.conversionRate == null ? '—' : `${a.conversionRate}%`}</td>
                  <td>{fmtHours(a.avgCreatedToContractedHours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header" style={{ display: 'flex', alignItems: 'center' }}>
          <div className="table-title">سرعة الإنجاز</div>
          <span style={{ flex: 1 }} />
          {[['agent', 'حسب الموظف'], ['department', 'حسب القسم'], ['type', 'حسب النوع']].map(([k, l]) => (
            <button key={k} className={`btn btn-sm ${groupBy === k ? 'btn-primary' : 'btn-outline'}`}
              style={{ marginRight: 6 }} onClick={() => setGroupBy(k)}>{l}</button>
          ))}
        </div>
        <div className="crm-cardnote">
          من الإنشاء حتى التعاقد: <b>{fmtHours(d.speed.overall.createdToContracted.avgHours)}</b>
          {' '}متوسط · الوسيط {fmtHours(d.speed.overall.createdToContracted.medianHours)} · {d.speed.overall.completedCount} صفقة مكتملة
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr>
              <th>{d.groupByLabel}</th><th>صفقات مكتملة</th>
              {d.speed.segments.map((s) => <th key={s.key}>{s.label}</th>)}
              <th>الإجمالي</th>
            </tr></thead>
            <tbody>
              {d.speed.groups.length === 0 ? (
                <tr><td colSpan={d.speed.segments.length + 3} className="crm-empty">لا توجد صفقات مكتملة</td></tr>
              ) : d.speed.groups.map((g) => (
                <tr key={g.key}>
                  <td style={{ fontWeight: 600 }}>{g.key}</td>
                  <td>{g.completedCount}</td>
                  {d.speed.segments.map((s) => <td key={s.key}>{fmtHours(g.segments[s.key].avgHours)}</td>)}
                  <td><b>{fmtHours(g.createdToContracted.avgHours)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Batch 2 (item 5): each timing is edited as a value + unit (ساعات/دقائق). The
// backend stores hours as the base and minutes as an optional override; the
// effective value it actually uses = minutes-if-set, else hours*60. On load we
// pick the unit from whichever field is populated; on save we send the chosen
// unit's field and clear the other so there is never an ambiguous pair.
// ---------------------------------------------------------------------------
// Batch 3 — classification breakdown.
//
// Reads the additive `classification` block the dashboard endpoint returns. It
// is rendered as its OWN section rather than as more KPIs on the strip above,
// because these numbers describe a different population: the KPIs count real
// opportunities, these count what the auto-scan captured and how humans judged
// it. Merging them into one row would invite reading an archived call as a
// pipeline outcome — exactly the confusion the backend split exists to prevent.
//
// The archived buckets are rendered from `archived.byType`, never hardcoded, so
// this panel follows the backend's list: 'متابعة طلب سابق' today, plus any
// retired bucket that still has historical rows behind it.
//
// Guarded on `c` so a frontend running against a backend without the block
// renders nothing rather than crashing.
// ---------------------------------------------------------------------------
const ClassificationPanel = ({ d }) => {
  const c = d.classification;
  if (!c) return null;

  const pending = c.pending || { count: 0, byAgent: [] };
  const archived = c.archived || { count: 0, byType: [] };
  const real = c.real || { count: 0 };
  const totalClassified = real.count + archived.count;

  return (
    <div className="table-container" style={{ marginBottom: 16 }}>
      <div className="table-header">
        <div className="table-title">تصنيف التواصل — المكالمات التي أنشأها النظام</div>
      </div>
      <div style={{ padding: 16 }}>
        <div className="crm-classbox" style={{ marginBottom: 14 }}>
          <div className="cell pending">
            <div className="n">{pending.count}</div>
            <div className="l">بانتظار التصنيف</div>
          </div>
          <div className="cell real">
            <div className="n">{real.count}</div>
            <div className="l">فرصة حقيقية</div>
          </div>
          {archived.byType.map((b) => (
            <div key={b.key} className="cell">
              <div className="n">{b.count}</div>
              <div className="l">{b.label}</div>
            </div>
          ))}
        </div>

        <div className="crm-hint" style={{ lineHeight: 1.8 }}>
          {totalClassified > 0
            ? `من ${totalClassified} مكالمة مصنَّفة، ${real.count} كانت فرصاً حقيقية `
              + `(${Math.round((real.count / totalClassified) * 100)}%). `
            : 'لم يتم تصنيف أي مكالمة بعد. '}
          المكالمات المؤرشفة لا تُحتسب ضمن الفرص ولا ضمن الخسائر، ولا تؤثر على
          نسبة التحويل.
        </div>

        {pending.byAgent && pending.byAgent.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--crm-border-soft)' }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--crm-text-muted)', marginBottom: 8 }}>
              كم مكالمة تنتظر التصنيف لدى كل موظف
            </div>
            <PendingByAgent rows={pending.byAgent} />
          </div>
        )}
      </div>
    </div>
  );
};

const PendingByAgent = ({ rows }) => {
  const { nameOf } = useAgentNames();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {rows.map((r) => (
        <span key={r.extension} className="crm-pill crm-pill-pending">
          {r.extension === '—' ? 'غير مسند' : (nameOf(r.extension) || r.extension)} · {r.count}
        </span>
      ))}
    </div>
  );
};

const SettingsTab = ({ flash }) => {
  const [rows, setRows] = useState([]);

  const load = useCallback(() => crmGetSettings().then((r) => setRows(r.data.settings.map((s) => ({
    ...s,
    // editable working copy: value + unit per timing
    windowUnit: s.window_minutes != null ? 'minutes' : 'hours',
    windowValue: s.window_minutes != null ? s.window_minutes : s.window_hours,
    intervalUnit: s.reminder_interval_minutes != null ? 'minutes' : 'hours',
    intervalValue: s.reminder_interval_minutes != null ? s.reminder_interval_minutes : s.reminder_interval_hours
  })))), []);
  useEffect(() => { load(); }, [load]);

  const set = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  // Effective minutes for a value+unit pair (mirrors the backend rule).
  const effMin = (value, unit) => {
    const n = parseInt(value, 10);
    if (!Number.isInteger(n) || n <= 0) return NaN;
    return unit === 'minutes' ? n : n * 60;
  };

  const save = async () => {
    try {
      await crmSaveSettings(rows.map((r) => {
        const payload = { stage_key: r.stage_key, max_reminders: parseInt(r.max_reminders, 10) };
        // window: send the chosen unit's field, clear the other
        if (r.windowUnit === 'minutes') { payload.window_minutes = parseInt(r.windowValue, 10); }
        else { payload.window_hours = parseInt(r.windowValue, 10); payload.window_minutes = null; }
        // interval: same
        if (r.intervalUnit === 'minutes') { payload.reminder_interval_minutes = parseInt(r.intervalValue, 10); }
        else { payload.reminder_interval_hours = parseInt(r.intervalValue, 10); payload.reminder_interval_minutes = null; }
        return payload;
      }));
      flash('تم حفظ الإعدادات');
      load();
    } catch (e) { flash(e.response?.data?.error || 'فشل الحفظ'); }
  };

  const UnitPicker = ({ i, valueKey, unitKey, r }) => (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input className="filter-input" style={{ width: 76 }} value={r[valueKey]}
        onChange={(e) => set(i, valueKey, e.target.value)} />
      <select className="filter-select" style={{ width: 90, fontSize: 13 }} value={r[unitKey]}
        onChange={(e) => set(i, unitKey, e.target.value)}>
        <option value="hours">ساعات</option>
        <option value="minutes">دقائق</option>
      </select>
    </div>
  );

  return (
    <div className="table-container">
      <div className="table-header"><div className="table-title">متى يُذكَّر الموظف ومتى يُسحب منه الطلب</div></div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr>
            <th>المرحلة</th><th>مهلة أول تذكير</th><th>الفاصل بين التذكيرات</th>
            <th>عدد التذكيرات</th><th>ماذا يعني ذلك</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const wMin = effMin(r.windowValue, r.windowUnit);
              const ivMin = effMin(r.intervalValue, r.intervalUnit);
              const m = parseInt(r.max_reminders, 10);
              const ok = Number.isInteger(m) && m > 0 && Number.isFinite(wMin) && Number.isFinite(ivMin);
              const unassignMin = ok ? wMin + m * ivMin : NaN;
              return (
                <tr key={r.stage_key}>
                  <td><span className={stagePillClass(r.stage_key)}>{STAGE_LABEL[r.stage_key]}</span></td>
                  <td><UnitPicker i={i} valueKey="windowValue" unitKey="windowUnit" r={r} /></td>
                  <td><UnitPicker i={i} valueKey="intervalValue" unitKey="intervalUnit" r={r} /></td>
                  <td><input className="filter-input" style={{ width: 70 }} value={r.max_reminders}
                    onChange={(e) => set(i, 'max_reminders', e.target.value)} /></td>
                  <td className={ok ? 'crm-muted' : 'crm-txt-danger'} style={{ fontSize: 12.5 }}>
                    {ok
                      ? `أول تذكير بعد ${fmtMinutes(wMin)}، ثم كل ${fmtMinutes(ivMin)}، حتى ${m} تذكيرات، ثم يُسحب الإسناد بعد ${fmtMinutes(unassignMin)}.`
                      : 'كل القيم يجب أن تكون أرقاماً صحيحة أكبر من صفر'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="crm-cardfoot"><button className="btn btn-primary" onClick={save}>حفظ الإعدادات</button></div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const TypesTab = ({ flash }) => {
  const [types, setTypes] = useState([]);
  const [name, setName] = useState('');
  const [dept, setDept] = useState('');
  const load = useCallback(() => crmGetTypes(true).then((r) => setTypes(r.data.types)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { await crmCreateType({ type_name: name, department: dept, sort_order: types.length + 1 }); setName(''); setDept(''); flash('تمت الإضافة'); load(); }
    catch (e) { flash(e.response?.data?.error || 'فشل'); }
  };
  const toggle = async (t) => {
    try { await crmUpdateType(t.id, { is_active: !t.is_active }); load(); }
    catch (e) { flash(e.response?.data?.error || 'فشل'); }
  };

  return (
    <div className="table-container">
      <div className="table-header"><div className="table-title">النوع يحدد القسم المسؤول تلقائياً عند إنشاء أي فرصة</div></div>
      <div className="crm-inlineform">
        <input className="filter-input" placeholder="اسم النوع" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="filter-input" placeholder="القسم المسؤول" value={dept} onChange={(e) => setDept(e.target.value)} />
        <button className="btn btn-primary btn-sm" onClick={add}>+ إضافة</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr><th>الترتيب</th><th>اسم النوع</th><th>القسم</th><th>الحالة</th><th></th></tr></thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.id}>
                <td>{t.sort_order}</td>
                <td style={{ fontWeight: 600 }}>{t.type_name}</td>
                <td>{t.department || '—'}</td>
                <td><span className={t.is_active ? 'crm-pill crm-pill-success' : 'crm-pill crm-pill-neutral'}>
                  {t.is_active ? 'مفعّل' : 'موقوف'}</span></td>
                <td><button className="btn btn-outline btn-sm" onClick={() => toggle(t)}>
                  {t.is_active ? 'إيقاف' : 'تفعيل'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// This tab is the answer to "how does the system know an agent's department".
const DepartmentsTab = ({ flash }) => {
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [ext, setExt] = useState('');
  const [dept, setDept] = useState('');

  const load = useCallback(() => {
    crmGetUserDepartments().then((r) => setRows(r.data.flat));
    crmGetTypes().then((r) => setDepartments([...new Set(r.data.types.map((t) => t.department).filter(Boolean))]));
    const token = localStorage.getItem('token');
    fetch('/api/auth/users', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setAgents(Array.isArray(d) ? d.filter((u) => u.extension) : []))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { await crmAddUserDepartment(ext, dept); flash('تم الربط'); load(); }
    catch (e) { flash(e.response?.data?.error || 'فشل الربط'); }
  };
  const remove = async (id) => {
    try { await crmDeleteUserDepartment(id); load(); } catch (e) { flash('فشل الحذف'); }
  };

  const byExt = rows.reduce((a, r) => { (a[r.extension] = a[r.extension] || []).push(r); return a; }, {});

  return (
    <div className="table-container">
      <div className="table-header"><div className="table-title">
        ربط الموظفين بالأقسام — يحدد من تصله المتابعات الموجّهة لقسم معيّن (يمكن ربط الموظف بأكثر من قسم)
      </div></div>
      <div className="crm-inlineform">
        <select className="filter-select" value={ext} onChange={(e) => setExt(e.target.value)}>
          <option value="">اختر الموظف</option>
          {agents.map((a) => <option key={a.extension} value={a.extension}>{a.fullName} ({a.extension})</option>)}
        </select>
        <select className="filter-select" value={dept} onChange={(e) => setDept(e.target.value)}>
          <option value="">اختر القسم</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={add} disabled={!ext || !dept}>+ ربط</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr><th>الموظف</th><th>الأقسام</th></tr></thead>
          <tbody>
            {Object.keys(byExt).length === 0 ? (
              <tr><td colSpan="2" className="crm-empty">لا توجد روابط</td></tr>
            ) : Object.entries(byExt).map(([e, list]) => {
              const agent = agents.find((a) => a.extension === e);
              return (
                <tr key={e}>
                  <td style={{ fontWeight: 600 }}>{agent ? `${agent.fullName} (${e})` : e}</td>
                  <td>
                    {list.map((r) => (
                      <span key={r.id} className="crm-chip">
                        {r.department}
                        <button className="x" onClick={() => remove(r.id)} title="حذف">×</button>
                      </span>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
const PoolTab = ({ flash, setActivePage }) => {
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [pick, setPick] = useState({});

  const load = useCallback(() => {
    crmGetUnassigned().then((r) => setRows(r.data.opportunities));
    const token = localStorage.getItem('token');
    fetch('/api/auth/users', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setAgents(Array.isArray(d) ? d.filter((u) => u.extension && u.role !== 'admin') : []))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async (id) => {
    try { await crmAssign(id, pick[id] || (agents[0] && agents[0].extension)); flash('تم الإسناد'); load(); }
    catch (e) { flash(e.response?.data?.error || 'فشل الإسناد'); }
  };

  return (
    <div className="table-container">
      <div className="table-header"><div className="table-title">
        فرص سحب النظام إسنادها بعد استنفاد التذكيرات ({rows.length})
      </div></div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr>
            <th>العميل</th><th>نوع الطلب</th><th>المرحلة</th><th>القسم</th>
            <th>الموظف السابق</th><th>مدة عدم الإسناد</th><th>إعادة الإسناد</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan="7" className="crm-empty">لا توجد فرص غير مسندة — كل شيء تحت السيطرة</td></tr>
            ) : rows.map((o) => (
              <tr key={o.id}>
                <td>
                  <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '2px 8px' }}
                    onClick={() => { crmNav.setClient(o.phone_number); setActivePage('crm-client'); }}>
                    {o.client_name || o.phone_number}
                  </button>
                </td>
                <td>{o.opportunity_type}</td>
                <td><span className={stagePillClass(o.crm_stage)}>{STAGE_LABEL[o.crm_stage]}</span></td>
                <td>{o.department || '—'}</td>
                <td>{o.previous_agent || '—'}</td>
                <td>{o.hours_unassigned != null ? fmtHours(o.hours_unassigned) : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select className="filter-select" style={{ fontSize: 12 }}
                      value={pick[o.id] || ''} onChange={(e) => setPick({ ...pick, [o.id]: e.target.value })}>
                      <option value="">اختر موظفاً</option>
                      {agents.map((a) => <option key={a.extension} value={a.extension}>{a.fullName}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={() => assign(o.id)}>إسناد</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Item 2: manager transfer-tracking monitor (read-only, GET /crm/transfers/all).
const TransfersMonitorTab = () => {
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [f, setF] = useState({ status: '', from_extension: '', to_extension: '', to_department: '', dateFrom: '', dateTo: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { crmUserNames().then((r) => setAgents(r.data.users || [])).catch(() => setAgents([])); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await crmAllTransfers(params);
      setRows(r.data.transfers || []);
    } catch (e) { setRows([]); } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const clear = () => setF({ status: '', from_extension: '', to_extension: '', to_department: '', dateFrom: '', dateTo: '' });

  return (
    // Batch 5 gave this tab the calm identity via a local `crm-calm` wrapper;
    // Batch 7 moved that scope to the whole admin page, so the wrapper is gone.
    // Styling only — same filters, columns, read-only GET and data flow.
    <div className="table-container">
      <div className="table-header"><div className="table-title">كل التحويلات — إلى أين ذهبت ومن استلمها</div></div>
      <div className="crm-inlineform">
        <select className="filter-select" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          <option value="">كل الحالات</option>
          <option value="pending">بانتظار الاستلام</option>
          <option value="picked_up">تم الاستلام</option>
          <option value="done">منتهي</option>
        </select>
        <select className="filter-select" value={f.from_extension} onChange={(e) => setF({ ...f, from_extension: e.target.value })}>
          <option value="">من: الكل</option>
          {agents.map((a) => <option key={a.extension} value={a.extension}>من: {a.fullName}</option>)}
        </select>
        <select className="filter-select" value={f.to_extension} onChange={(e) => setF({ ...f, to_extension: e.target.value })}>
          <option value="">إلى موظف: الكل</option>
          {agents.map((a) => <option key={a.extension} value={a.extension}>إلى: {a.fullName}</option>)}
        </select>
        <input className="filter-input" placeholder="إلى قسم..." value={f.to_department}
          onChange={(e) => setF({ ...f, to_department: e.target.value })} style={{ width: 140 }} />
        <input type="date" className="filter-input" value={f.dateFrom} onChange={(e) => setF({ ...f, dateFrom: e.target.value })} style={{ width: 150 }} />
        <input type="date" className="filter-input" value={f.dateTo} onChange={(e) => setF({ ...f, dateTo: e.target.value })} style={{ width: 150 }} />
        {Object.values(f).some(Boolean) && <button className="btn btn-outline btn-sm" onClick={clear}>مسح</button>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead><tr>
            <th>العميل</th><th>من</th><th>إلى</th><th>الحالة</th><th>من استلمها</th><th>وقت التحويل</th><th>وقت الاستلام</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="crm-empty">جاري التحميل...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="7" className="crm-empty">لا توجد تحويلات مطابقة</td></tr>
            ) : rows.map((t) => {
              const st = TRANSFER_STATUS[t.status] || { label: t.status, pill: 'crm-pill-neutral' };
              return (
                <tr key={t.id}>
                  <td style={{ direction: 'ltr', textAlign: 'right' }}>{t.phone_number}</td>
                  <td>{t.from_name}</td>
                  <td>{t.to_extension ? t.to_extension_name : (t.to_department ? `قسم ${t.to_department}` : '—')}</td>
                  <td><span className={`crm-pill ${st.pill}`}>{st.label}</span></td>
                  <td>{t.picked_up_by_name || '—'}</td>
                  <td style={{ fontSize: 12 }}>{fmtDateTime(t.created_at)}</td>
                  <td style={{ fontSize: 12 }}>{t.picked_up_at ? fmtDateTime(t.picked_up_at) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Item 3: view-as-employee (READ-ONLY). Fetches a chosen agent's pipeline and
// transfer inbox via the admin /crm/as-user/:ext/* endpoints. No action buttons
// are rendered in this mode — it is deliberately observe-only.
const ViewAsTab = () => {
  const [agents, setAgents] = useState([]);
  const [ext, setExt] = useState('');
  const [opps, setOpps] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { crmUserNames().then((r) => setAgents(r.data.users || [])).catch(() => setAgents([])); }, []);

  const view = useCallback(async (e) => {
    if (!e) { setOpps([]); setTransfers([]); return; }
    setLoading(true);
    try {
      const [o, t] = await Promise.all([crmAsUserOpportunities(e), crmAsUserTransfers(e)]);
      setOpps(o.data.opportunities || []);
      setTransfers(t.data.transfers || []);
    } catch (err) { setOpps([]); setTransfers([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { view(ext); }, [ext, view]);

  const agentName = (agents.find((a) => a.extension === ext) || {}).fullName || ext;

  return (
    <div>
      <div className="crm-toolbar">
        <span className="crm-muted" style={{ fontSize: 13 }}>اختر موظفاً لعرض شاشته:</span>
        <select className="filter-select" value={ext} onChange={(e) => setExt(e.target.value)}>
          <option value="">— اختر موظفاً —</option>
          {agents.map((a) => <option key={a.extension} value={a.extension}>{a.fullName} ({a.extension})</option>)}
        </select>
        {ext && <button className="btn btn-outline btn-sm" onClick={() => setExt('')}>خروج من وضع العرض</button>}
      </div>

      {!ext ? (
        <div className="table-container"><div className="crm-empty">
          اختر موظفاً لعرض فرصه وصندوق تحويلاته — للقراءة فقط.
        </div></div>
      ) : (
        <>
          <div className="crm-viewas-banner">
            👁️ عرض للقراءة فقط — بعين الموظف <b>{agentName}</b>. لا يمكن تنفيذ أي إجراء في هذا الوضع.
          </div>

          <div className="table-container" style={{ marginBottom: 16 }}>
            <div className="table-header"><div className="table-title">فرص {agentName} النشطة ({opps.length})</div></div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr><th>العميل</th><th>نوع الطلب</th><th>المرحلة</th><th>القسم</th><th>مدة المرحلة</th><th>التذكيرات</th></tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan="6" className="crm-empty">جاري التحميل...</td></tr>
                    : opps.length === 0 ? <tr><td colSpan="6" className="crm-empty">لا توجد فرص نشطة</td></tr>
                      : opps.map((o) => (
                        <tr key={o.id}>
                          <td>{o.client_name || o.phone_number}</td>
                          <td>{o.opportunity_type}</td>
                          <td><span className={stagePillClass(o.crm_stage)}>{STAGE_LABEL[o.crm_stage]}</span></td>
                          <td>{o.department || '—'}</td>
                          <td>{fmtHours(o.hoursInStage)}</td>
                          <td>{o.reminder_count || 0}</td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="table-container">
            <div className="table-header"><div className="table-title">صندوق تحويلات {agentName} ({transfers.length})</div></div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr><th>العميل</th><th>من</th><th>موجّه إلى</th><th>الملاحظة</th><th>الوقت</th></tr></thead>
                <tbody>
                  {transfers.length === 0 ? <tr><td colSpan="5" className="crm-empty">لا توجد تحويلات معلقة</td></tr>
                    : transfers.map((t) => (
                      <tr key={t.id}>
                        <td style={{ direction: 'ltr', textAlign: 'right' }}>{t.phone_number}</td>
                        <td>{t.from_name || t.from_extension}</td>
                        <td>{t.to_extension ? `موظف ${t.to_extension}` : (t.to_department ? `قسم ${t.to_department}` : '—')}</td>
                        <td style={{ fontSize: 12, maxWidth: 260 }}>{t.note}</td>
                        <td style={{ fontSize: 12 }}>{fmtDateTime(t.created_at)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CrmAdminPage;
